/**
 * User Store
 * Manages user profile and preferences
 * 
 * @author Varun Pandey
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UserState {
  preferences: {
    theme: 'light' | 'dark' | 'system';
    language: string;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
  };
  
  // Actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setLanguage: (language: string) => void;
  updateNotificationPreferences: (preferences: Partial<UserState['preferences']['notifications']>) => void;
  resetPreferences: () => void;
}

const defaultPreferences = {
  theme: 'system' as const,
  language: 'en',
  notifications: {
    email: true,
    push: true,
    sms: false,
  },
};

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      preferences: defaultPreferences,

      setTheme: (theme) => {
        set((state) => ({
          preferences: { ...state.preferences, theme },
        }));
      },

      setLanguage: (language) => {
        set((state) => ({
          preferences: { ...state.preferences, language },
        }));
      },

      updateNotificationPreferences: (newPreferences) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            notifications: {
              ...state.preferences.notifications,
              ...newPreferences,
            },
          },
        }));
      },

      resetPreferences: () => {
        set({ preferences: defaultPreferences });
      },
    }),
    {
      name: 'user-preferences',
    }
  )
);
